import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, RefreshCw, Trash2 } from "lucide-react";

const BACKEND_URL = (typeof window !== 'undefined' && process.env.NODE_ENV === 'production')
  ? window.location.origin
  : (process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' ? window.location.origin : ''));
const API = `${BACKEND_URL}/api`;

const fmtTime = (ts) => new Date(ts * 1000).toLocaleString("it-IT");
const fmtMin = (s) => `${Math.floor((s || 0) / 60)}m ${(s || 0) % 60}s`;
const typeLabel = (t) => (t === "live" ? "Canale TV" : t === "movie" ? "Film" : "Serie");

const DiagnosticsPage = () => {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/admin/playback-errors`, { params: { limit: 200 } })
      .then((r) => { setData(r.data); setLoadError(false); })
      .catch(() => setLoadError(true));
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 20000);
    return () => clearInterval(iv);
  }, [load]);

  const clearAll = async () => {
    await axios.delete(`${API}/admin/playback-errors`).catch(() => {});
    load();
  };

  return (
    <div className="space-y-6" data-testid="diagnostics-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-headings text-4xl mb-1">Diagnostica Player</h1>
          <p className="text-zinc-500 text-sm">
            Ogni interruzione di riproduzione registrata dalle app dei clienti, con il codice
            tecnico reale. Serve a capire se la causa è il provider, la rete o l'app.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="diag-refresh-btn"
            onClick={load}
            className="flex items-center gap-2 px-4 py-2 bg-zinc-900 border border-zinc-700 rounded-sm text-sm hover:border-netflix-red transition-colors"
          >
            <RefreshCw className="w-4 h-4" /> Aggiorna
          </button>
          <button
            data-testid="diag-clear-btn"
            onClick={clearAll}
            className="flex items-center gap-2 px-4 py-2 bg-zinc-900 border border-zinc-700 rounded-sm text-sm hover:border-netflix-red transition-colors"
          >
            <Trash2 className="w-4 h-4" /> Svuota
          </button>
        </div>
      </div>

      {loadError && (
        <p className="text-amber-400 text-sm" data-testid="diag-load-error">
          Impossibile leggere la diagnostica: riprovo automaticamente…
        </p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-5" data-testid="diag-total-card">
          <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Interruzioni registrate</p>
          <p className="text-3xl font-bold">{data ? data.total : "—"}</p>
          <p className="text-xs text-zinc-500 mt-1">ultimi 14 giorni</p>
        </div>
        <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-5" data-testid="diag-fatal-card">
          <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Finite con errore a schermo</p>
          <p className={`text-3xl font-bold ${data && data.fatal > 0 ? "text-netflix-red" : "text-green-500"}`}>
            {data ? data.fatal : "—"}
          </p>
          <p className="text-xs text-zinc-500 mt-1">le altre si sono riprese da sole</p>
        </div>
        <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-5" data-testid="diag-topcode-card">
          <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Causa più frequente</p>
          <p className="text-xl font-bold font-mono truncate">
            {data && data.by_error.length ? data.by_error[0].error_code : "—"}
          </p>
          <p className="text-xs text-zinc-500 mt-1">
            {data && data.by_error.length
              ? `${data.by_error[0].count} volte${data.by_error[0].http_code ? ` · HTTP ${data.by_error[0].http_code}` : ""}`
              : "nessun dato"}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-5">
          <h2 className="font-bold mb-4 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-netflix-red" /> Per tipo di errore
          </h2>
          {!data || data.by_error.length === 0 ? (
            <p className="text-zinc-500 text-sm py-4" data-testid="diag-no-errors">
              Nessuna interruzione registrata: le riproduzioni stanno filando liscie.
            </p>
          ) : (
            <div className="space-y-2" data-testid="diag-by-error">
              {data.by_error.map((r) => (
                <div key={`${r.error_code}-${r.http_code}`} className="flex items-center justify-between py-1.5 border-b border-zinc-800/50 last:border-0">
                  <span className="text-sm font-mono truncate mr-3">
                    {r.error_code}{r.http_code ? ` · HTTP ${r.http_code}` : ""}
                  </span>
                  <span className="text-sm text-zinc-300 whitespace-nowrap">
                    {r.count} <span className="text-zinc-500">({r.recovered} recuperate)</span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-5">
          <h2 className="font-bold mb-4">Contenuti più problematici</h2>
          {!data || data.by_content.length === 0 ? (
            <p className="text-zinc-500 text-sm py-4">Nessun contenuto segnalato.</p>
          ) : (
            <div className="space-y-2" data-testid="diag-by-content">
              {data.by_content.map((r) => (
                <div key={`${r.type}-${r.name}`} className="flex items-center justify-between py-1.5 border-b border-zinc-800/50 last:border-0">
                  <span className="text-sm truncate mr-3">{r.name}</span>
                  <span className="text-sm text-zinc-300 whitespace-nowrap">
                    {r.count} <span className="text-zinc-500">{typeLabel(r.type)}</span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-5">
        <h2 className="font-bold mb-4">Ultime interruzioni</h2>
        {!data || data.items.length === 0 ? (
          <p className="text-zinc-500 text-sm py-4">Nessun evento da mostrare.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-sm" data-testid="diag-table">
              <thead className="text-zinc-500 text-xs uppercase">
                <tr>
                  <th className="text-left py-2">Quando</th>
                  <th className="text-left py-2">Cliente</th>
                  <th className="text-left py-2">Contenuto</th>
                  <th className="text-left py-2">Al minuto</th>
                  <th className="text-left py-2">Dopo</th>
                  <th className="text-left py-2">Via</th>
                  <th className="text-left py-2">Errore</th>
                  <th className="text-left py-2">Esito</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {data.items.map((r, i) => (
                  <tr key={`${r.created_at}-${i}`}>
                    <td className="py-2 text-zinc-400 whitespace-nowrap">{fmtTime(r.created_at)}</td>
                    <td className="py-2 font-medium">{r.username || "—"}</td>
                    <td className="py-2 text-zinc-300 max-w-[220px] truncate">{r.stream_name || typeLabel(r.stream_type)}</td>
                    <td className="py-2 text-zinc-400">{fmtMin(r.position_sec)}</td>
                    <td className="py-2 text-zinc-400">{fmtMin(r.played_sec)}</td>
                    <td className="py-2 text-zinc-400">{r.route === "direct" ? "Diretto" : "VPS"}</td>
                    <td className="py-2 font-mono text-xs">
                      {r.error_code}{r.http_code ? ` · HTTP ${r.http_code}` : ""}
                      {r.detail ? <span className="block text-zinc-500 truncate max-w-[260px]">{r.detail}</span> : null}
                    </td>
                    <td className={`py-2 ${r.recovered ? "text-green-500" : "text-netflix-red"}`}>
                      {r.recovered ? "ripresa" : "errore a schermo"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default DiagnosticsPage;
